import styled from 'styled-components';
import { AsFileUpload } from '@ardoq/dropzone';
import { Icon, IconName } from '@ardoq/icons';
import { t, type I18nLocale } from '@ardoq/i18n';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { Switch } from '@ardoq/forms';
import { WithPopover } from '@ardoq/popovers';
import { colors, r4, s4, s8 } from '@ardoq/design-tokens';
import { MAX_DOCUMENTS_LIMIT } from '../../constants';
import { SUPPORTED_DOCUMENT_TYPES } from '../utils';

const MenuItemButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${s8};
  width: 100%;
  padding: ${s8};
  border: none;
  background: transparent;
  border-radius: ${r4};
  cursor: pointer;
  text-align: left;
  white-space: nowrap;

  &[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus-visible,
  &:hover:not([aria-disabled='true']) {
    background-color: ${colors.surfaceHover};
  }
`;

type MenuItemContentProps = {
  icon: IconName;
  label: string;
};

const MenuItemContent = ({ icon, label }: MenuItemContentProps) => (
  <>
    <Icon color={colors.textSubtle} iconName={icon} />
    <Text variant="text2">{label}</Text>
  </>
);

type GiveContextMenuProps = {
  i18nLocale: I18nLocale;
  isSelectionLimitReached: boolean;
  onSelectOrgDocuments: VoidFunction;
  onUploadFromComputer: (files: File[]) => void;
  webSearchEnabled: boolean;
  hasWebSearchFeature: boolean;
  isRequestPending: boolean;
  onToggleWebSearch: (enabled: boolean) => void;
};

export const GiveContextMenu = ({
  i18nLocale,
  isSelectionLimitReached,
  onSelectOrgDocuments,
  onUploadFromComputer,
  webSearchEnabled,
  hasWebSearchFeature,
  isRequestPending,
  onToggleWebSearch,
}: GiveContextMenuProps) => {
  const disabledTooltip = t(
    i18nLocale,
    'assistant:documents.selectionLimitReached',
    { count: MAX_DOCUMENTS_LIMIT },
    'You can add up to {{count}} documents. Deselect one to add another.'
  );
  return (
    <Box padding="xsmall">
      <Stack gap="none">
        <AsFileUpload
          multiple
          accept={SUPPORTED_DOCUMENT_TYPES.acceptedFileTypes}
          isDisabled={isSelectionLimitReached}
          onFiles={onUploadFromComputer}
        >
          <WithPopover content={isSelectionLimitReached ? disabledTooltip : ''}>
            <MenuItemButton
              style={{ color: colors.grey950 }}
              aria-disabled={isSelectionLimitReached}
            >
              <MenuItemContent
                icon={IconName.COMPUTER}
                label={t(
                  i18nLocale,
                  'assistant:documents.addFromComputer',
                  'Add from your computer'
                )}
              />
            </MenuItemButton>
          </WithPopover>
        </AsFileUpload>
        <WithPopover content={isSelectionLimitReached ? disabledTooltip : ''}>
          <MenuItemButton
            style={{ color: colors.grey950 }}
            aria-disabled={isSelectionLimitReached}
            onClick={isSelectionLimitReached ? undefined : onSelectOrgDocuments}
          >
            <MenuItemContent
              icon={IconName.ARCHIVE}
              label={t(
                i18nLocale,
                'assistant:documents.addFromOrgDocuments',
                'Add from Organization documents'
              )}
            />
          </MenuItemButton>
        </WithPopover>
        {hasWebSearchFeature && (
          <WebSearchToggle
            role="button"
            aria-disabled={isRequestPending}
            aria-label={t(
              i18nLocale,
              'assistant:webSearch.toggleAriaLabel',
              'Toggle web search'
            )}
            onClick={
              isRequestPending
                ? undefined
                : () => onToggleWebSearch(!webSearchEnabled)
            }
          >
            <FlexBox align="center" gap="small">
              <Icon color={colors.textSubtle} iconName={IconName.GLOBE} />
              <Text variant="text2" color="textDefault">
                {t(i18nLocale, 'assistant:webSearch.menuLabel', 'Web search')}
              </Text>
            </FlexBox>
            <FlexBox align="center">
              <Switch
                name="assistant-web-search-toggle"
                isChecked={webSearchEnabled}
                isDisabled={isRequestPending}
                // change handler is driven by parent container. passing empty func to avoid warning.
                onChange={() => {}}
                alignItemsCenter
              />
            </FlexBox>
          </WebSearchToggle>
        )}
      </Stack>
    </Box>
  );
};

const WebSearchToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${s4};
  width: 100%;
  padding: ${s4} ${s8};
  cursor: pointer;
  &[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
  }
  &:hover:not([aria-disabled='true']) {
    background: ${colors.surfaceHover};
  }
`;
